import React from 'react';
import { FlipCard } from '@/components/ui/FlipCard';
import { VondiLogo } from '@/components/ui/VondiLogo';
import { GlassSurface } from '@/components/ui/GlassSurface';
import { AhorroCifra } from '@/components/ui/AhorroCifra';
import { Button } from '@/components/ui/button';

/**
 * BrandLab — banco de pruebas visual del sistema VONDI.
 *
 * Existe para poder correr de verdad el checklist de verificación de
 * FLIPCARD_SPEC_v3 §9 en vez de darlo por bueno de memoria: pone en una sola
 * página las cuatro tarjetas de prueba del spec, los casos límite (texto
 * larguísimo, slots vacíos), la paleta completa, los cuatro estados y las
 * primitivas de marca.
 *
 * Ruta: /brand-lab
 */

/* Un logo de aliado de mentira, en blanco y con proporción configurable, para
   comprobar que la zona C nunca se deforma ni se desborda. */
function logoFalso(texto, ancho, alto) {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${ancho} ${alto}">
    <rect x="1" y="1" width="${ancho - 2}" height="${alto - 2}" fill="none" stroke="white" stroke-width="3"/>
    <text x="${ancho / 2}" y="${alto / 2}" fill="white" font-family="sans-serif"
      font-size="${Math.min(ancho / (texto.length * 0.62), alto * 0.6)}"
      font-weight="700" text-anchor="middle" dominant-baseline="central">${texto}</text>
  </svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

/* Las cuatro tarjetas que el spec §9 exige comparar: textos muy distintos que
   deben verse "del mismo tamaño y ritmo". */
const CUATRO_DEL_SPEC = [
  {
    storeName: 'L’Occitane',
    headlineNumber: '15%',
    headlineThin: 'de descuento',
    headlineStrong: 'ADICIONAL',
    scope: 'en toda la tienda',
    colorStart: '#FBA63C',
    allyLogo: logoFalso('L’OCCITANE', 300, 70),
    description: 'Aplica sobre precio de outlet.',
  },
  {
    storeName: 'Birkenstock',
    headlineNumber: '6',
    headlineThin: 'meses sin',
    headlineStrong: 'INTERESES',
    scope: 'en tu compra',
    colorStart: '#97B0F5',
    allyLogo: logoFalso('BIRKENSTOCK', 340, 60),
    description: 'Con tarjetas participantes.',
  },
  {
    storeName: 'A|X',
    headlineNumber: '2',
    headlineThin: 'playera basica por',
    headlineStrong: 'el precio de 1',
    scope: 'en toda la tienda',
    colorStart: '#2C3350',
    allyLogo: logoFalso('A|X', 120, 120),
    description: 'Aplica en línea básica.',
  },
  {
    storeName: 'Tripoli',
    headlineNumber: '1',
    headlineThin: 'agua de sabor',
    headlineStrong: 'GRATIS',
    scope: 'en tu pedido',
    colorStart: '#FF1F1F',
    allyLogo: logoFalso('TRIPOLI', 220, 90),
    description: 'Una por visita.',
  },
];

const CASOS_LIMITE = [
  {
    etiqueta: 'Texto larguísimo (debe encoger, nunca truncar ni desbordar)',
    benefit: {
      storeName: 'Tienda con nombre larguísimo',
      headlineNumber: '35%',
      headlineThin: 'de descuento acumulable sobre precio de outlet',
      headlineStrong: 'EN ABSOLUTAMENTE TODA LA TIENDA',
      scope: 'válido de lunes a jueves excepto días festivos',
      colorStart: '#F794B4',
      allyLogo: logoFalso('MARCA', 260, 80),
    },
  },
  {
    etiqueta: 'Solo número (el bloque se recentra)',
    benefit: { storeName: 'Solo número', headlineNumber: '2x1', colorStart: '#1A9C52' },
  },
  {
    etiqueta: 'Solo texto fino, sin número ni palabra fuerte',
    benefit: { storeName: 'Buen fin', headlineThin: 'Buen fin', colorStart: '#97B0F5' },
  },
  {
    etiqueta: 'Todo vacío salvo el logo del aliado',
    benefit: { storeName: 'Vacía', colorStart: '#6E6E77', allyLogo: logoFalso('SIN TEXTO', 240, 80) },
  },
  {
    etiqueta: 'Sin logo de aliado',
    benefit: {
      storeName: 'Sin logo',
      headlineNumber: '10%',
      headlineThin: 'de descuento',
      headlineStrong: 'ADICIONAL',
      scope: 'en toda la tienda',
      colorStart: '#FA4D0A',
    },
  },
];

/* Los colores que el spec §9 pide comprobar, más el marino oscuro que el
   propio spec advierte que rinde el gel con menos fuerza. */
const PALETA = [
  ['naranja', '#FA4D0A'],
  ['coral', '#F73E48'],
  ['rojo vivo', '#FF1F1F'],
  ['rosa', '#F794B4'],
  ['ámbar', '#FBA63C'],
  ['lavanda', '#97B0F5'],
  ['éxito', '#1A9C52'],
  ['marino (oscuro)', '#2C3350'],
];

const ESTADOS = [
  ['normal', 'Suscripción activa'],
  ['bloqueada', 'Usuario gratuito, card premium'],
  ['gratis', 'Canjeable sin pagar'],
  ['canjeada', 'Ya usada, sin acción'],
];

function Seccion({ titulo, nota, children }) {
  return (
    <section style={{ marginBottom: 64 }}>
      <h2 className="font-display" style={{ fontSize: 26, fontWeight: 600, color: '#0E0E10', margin: 0 }}>
        {titulo}
      </h2>
      {nota && <p style={{ color: '#6E6E77', margin: '6px 0 20px', maxWidth: 720 }}>{nota}</p>}
      {children}
    </section>
  );
}

function Rejilla({ children, min = 210 }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(auto-fill, minmax(${min}px, 1fr))`,
        gap: 22,
        alignItems: 'start',
      }}
    >
      {children}
    </div>
  );
}

export default function BrandLab() {
  return (
    <div style={{ background: '#FFFFFF', minHeight: '100vh', padding: '40px 24px 120px' }}>
      <div style={{ maxWidth: 1180, margin: '0 auto' }}>
        <header style={{ marginBottom: 48 }}>
          <VondiLogo size={44} withDescriptor />
          <h1
            className="font-display"
            style={{ fontSize: 38, fontWeight: 700, color: '#0E0E10', margin: '18px 0 6px', letterSpacing: '-0.02em' }}
          >
            Banco de pruebas del sistema visual
          </h1>
          <p style={{ color: '#6E6E77', margin: 0 }}>
            Checklist de verificación de FLIPCARD_SPEC_v3 §9 y primitivas de BRAND.md.
          </p>
        </header>

        <Seccion
          titulo="Las cuatro del spec"
          nota="Textos muy distintos que deben verse del mismo tamaño y con el mismo ritmo: misma altura de bloque, misma separación y misma alineación centrada."
        >
          <Rejilla>
            {CUATRO_DEL_SPEC.map((b) => (
              <FlipCard key={b.storeName} benefit={b} />
            ))}
          </Rejilla>
        </Seccion>

        <Seccion
          titulo="Casos límite"
          nota="El titular encoge hasta caber, nunca se trunca con puntos suspensivos y nunca desborda. Los slots vacíos no dejan hueco."
        >
          <Rejilla>
            {CASOS_LIMITE.map((c) => (
              <div key={c.etiqueta}>
                <FlipCard benefit={c.benefit} />
                <p style={{ fontSize: 12, color: '#6E6E77', marginTop: 8 }}>{c.etiqueta}</p>
              </div>
            ))}
          </Rejilla>
        </Seccion>

        <Seccion
          titulo="El gel sobre distintos colores"
          nota="La capa de gel es neutra, así que debe leerse bien en cualquier tono. En las tarjetas rojas el degradado tiene que ir hacia el rosa; en el marino, el spec ya advierte que el gel rinde con menos fuerza."
        >
          <Rejilla min={170}>
            {PALETA.map(([nombre, hex]) => (
              <div key={hex}>
                <FlipCard
                  benefit={{
                    storeName: nombre,
                    headlineNumber: '10%',
                    headlineThin: 'de descuento',
                    headlineStrong: 'ADICIONAL',
                    scope: 'en toda la tienda',
                    colorStart: hex,
                  }}
                />
                <p style={{ fontSize: 12, color: '#6E6E77', marginTop: 8 }}>
                  {nombre} · {hex}
                </p>
              </div>
            ))}
          </Rejilla>
        </Seccion>

        <Seccion
          titulo="Los cuatro estados"
          nota="En «bloqueada» el filtro se aplica al conjunto —degradado, gel y contenido— y el texto sigue legible: es el gancho de venta."
        >
          <Rejilla min={190}>
            {ESTADOS.map(([estado, desc]) => (
              <div key={estado}>
                <FlipCard
                  estado={estado}
                  benefit={{
                    storeName: 'Tous',
                    headlineNumber: '10%',
                    headlineThin: 'de descuento',
                    headlineStrong: 'ADICIONAL',
                    scope: 'en toda la tienda',
                    colorStart: '#F73E48',
                    allyLogo: logoFalso('TOUS', 200, 80),
                    description: 'Obtén 10% adicional en toda la tienda.',
                    terms: 'No acumulable con otras promociones. Vigencia al 31 de diciembre.',
                  }}
                />
                <p style={{ fontSize: 12, color: '#6E6E77', marginTop: 8 }}>
                  <strong>{estado}</strong> — {desc}
                </p>
              </div>
            ))}
          </Rejilla>
        </Seccion>

        <Seccion
          titulo="La misma tarjeta a distintos tamaños"
          nota="La proporción 7:10 y el radio son constantes, y el contenido escala con ellas. Cada caja tiene un ancho fijo; el alto que se ve debajo debe ser exactamente 10/7 de ese ancho."
        >
          <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            {[110, 160, 210, 280, 360].map((w) => (
              <div key={w} style={{ width: w }}>
                <FlipCard
                  flippable={false}
                  benefit={{
                    storeName: 'Puma',
                    headlineNumber: '20%',
                    headlineThin: 'de descuento',
                    headlineStrong: 'ADICIONAL',
                    scope: 'en toda la tienda',
                    colorStart: '#FF1F1F',
                    allyLogo: logoFalso('PUMA', 220, 80),
                  }}
                />
                <p style={{ fontSize: 12, color: '#6E6E77', marginTop: 8 }}>
                  {w}px de ancho → {Math.round((w * 10) / 7)}px de alto
                </p>
              </div>
            ))}
          </div>
        </Seccion>

        <Seccion
          titulo="El reverso"
          nota="El manual solo describe la cara frontal, así que el reverso se resuelve con el sistema de BRAND.md: vidrio sobre el color de la tarjeta, con el detalle y los T&C de la promoción. Sin gel — el acabado líquido es la firma del frente."
        >
          <Rejilla min={210}>
            {[
              {
                storeName: 'Tous',
                headlineNumber: '10%',
                headlineStrong: 'ADICIONAL',
                colorStart: '#F73E48',
                description: 'Obtén 10% de descuento adicional en toda la tienda, sobre precio de outlet.',
                terms: 'No acumulable con otras promociones.\nUna redención por visita.\nVigencia al 31 de diciembre de 2026.',
              },
              {
                storeName: 'Sin términos propios',
                headlineNumber: '2x1',
                colorStart: '#1A9C52',
                description: 'Lleva dos artículos y paga uno.',
              },
            ].map((b) => (
              <FlipCard key={b.storeName} benefit={b} caraInicial="reverso" />
            ))}
          </Rejilla>
        </Seccion>

        <Seccion
          titulo="Logos de aliado con proporciones distintas"
          nota="Cada logo escala para caber en la zona C sin deformarse y sin rebasarla."
        >
          <Rejilla min={170}>
            {[
              ['Muy ancho', 500, 60],
              ['Cuadrado', 120, 120],
              ['Muy alto', 70, 200],
              ['Panorámico', 700, 40],
            ].map(([n, w, h]) => (
              <div key={n}>
                <FlipCard
                  benefit={{
                    storeName: n,
                    headlineNumber: '20%',
                    headlineStrong: 'ADICIONAL',
                    scope: 'en toda la tienda',
                    colorStart: '#FA4D0A',
                    allyLogo: logoFalso(n.toUpperCase(), w, h),
                  }}
                />
                <p style={{ fontSize: 12, color: '#6E6E77', marginTop: 8 }}>
                  {n} · {w}×{h}
                </p>
              </div>
            ))}
          </Rejilla>
        </Seccion>

        <Seccion titulo="Logotipo" nota="Las tres versiones del manual, con su área de respeto y su tamaño mínimo.">
          <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'stretch' }}>
            <div style={{ background: '#FFFFFF', border: '1px solid #E7E7EC', borderRadius: 24, padding: 28 }}>
              <VondiLogo size={40} withDescriptor />
              <p style={{ fontSize: 12, color: '#6E6E77', marginTop: 12 }}>gradiente · sobre claro</p>
            </div>
            <div style={{ background: '#F5F5F7', borderRadius: 24, padding: 28 }}>
              <VondiLogo variant="negro" size={40} />
              <p style={{ fontSize: 12, color: '#6E6E77', marginTop: 12 }}>negro · monocromo</p>
            </div>
            <div
              style={{
                background: 'linear-gradient(100deg, #FA4D0A, #F73E48)',
                borderRadius: 24,
                padding: 28,
              }}
            >
              <VondiLogo variant="blanco" size={40} withDescriptor />
              <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.85)', marginTop: 12 }}>
                blanco · sobre gradiente
              </p>
            </div>
            <div style={{ background: '#0E0E10', borderRadius: 24, padding: 28 }}>
              <VondiLogo variant="blanco" size={12} />
              <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', marginTop: 12 }}>
                tamaño mínimo (28px de ancho)
              </p>
            </div>
          </div>
        </Seccion>

        <Seccion titulo="Botones y cifra de ahorro">
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center', marginBottom: 28 }}>
            <Button variant="vondi" size="pill">
              Comprar mi acceso
            </Button>
            <Button variant="vondiIdentidad" size="pill">
              Ver mi QR
            </Button>
            <Button variant="vondiSecundario" size="pill">
              Secundario
            </Button>
          </div>

          <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
            <GlassSurface style={{ padding: 28, minWidth: 260 }}>
              <p style={{ margin: 0, color: '#6E6E77', fontSize: 14 }}>Con tu acceso vondi ahorras</p>
              <AhorroCifra style={{ fontSize: 44 }}>$1,000</AhorroCifra>
            </GlassSurface>

            <div
              style={{
                background: 'linear-gradient(100deg, #FA4D0A, #FF1F1F)',
                borderRadius: 24,
                padding: 28,
                minWidth: 260,
              }}
            >
              <p style={{ margin: 0, color: 'rgba(255,255,255,0.9)', fontSize: 14 }}>Sobre fondo rojo</p>
              <AhorroCifra sobreFondoRojo style={{ fontSize: 44 }}>
                $1,000
              </AhorroCifra>
            </div>
          </div>
        </Seccion>

        <Seccion titulo="Tipografía" nota="Los titulares deben rendir en Poppins y el cuerpo en Inter.">
          <p className="font-display" style={{ fontSize: 52, fontWeight: 700, margin: 0, letterSpacing: '-0.02em' }}>
            Paga menos
          </p>
          <p className="font-display" style={{ fontSize: 36, fontWeight: 600, margin: '12px 0 0' }}>
            Cuánto puedes ahorrar
          </p>
          <p className="font-sans" style={{ fontSize: 20, fontWeight: 600, margin: '12px 0 0' }}>
            20% de descuento adicional
          </p>
          <p className="font-sans" style={{ fontSize: 16, margin: '12px 0 0', color: '#33333A' }}>
            Obtén descuentos reales en toda la tienda con tu QR.
          </p>
          <p
            className="font-sans"
            style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.12em', margin: '12px 0 0', color: '#6E6E77' }}
          >
            PRECIOS REALES
          </p>
        </Seccion>
      </div>
    </div>
  );
}
